/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useEffect, useState } from 'react';
import { Modal, Select, message } from 'antd';
import { changeAirlineCode, getAirlineCodes, getAirlineSystemModels } from '~lib/netconnClient';
import constants from '~util/constants';
import handleAuthErr from '~util/handleAuthErr';
import logger from '~util/logger';

const AirlineCodeChangeModal = ({ config, updateConfig, visible, onClose, systemModel = '' }) => {
  const [airlineCodes, setAirlineCodes] = useState();
  const [isValidAirline, setIsValidAirline] = useState(true);
  const [selectedAirlineCode, setSelectedAirlineCode] = useState();
  const [loading, setLoading] = useState({
    airlineCodes: false,
    airlineSystemModels: false,
    confirm: false,
  });

  const { Option } = Select;

  const close = () => {
    // Reset state, as the Modal component is not actually unmounted when closed (set to "not visible")
    setSelectedAirlineCode();
    onClose();
  };

  useEffect(() => {
    // Wait for config to load before fetching airlineCodes
    if (!!config) {
      setLoading(prev => ({ ...prev, airlineCodes: true }));
      getAirlineCodes()
        .then(data => {
          if (!!data?.length) {
            // We won't allow them to select the current airlineCode
            // nor the BA airlineCode (as only CA uses this action)
            const caAirlineCodes = data
              .filter(
                airlineCode =>
                  airlineCode !== constants.BA &&
                  airlineCode !== constants.BENSENVILLE_CODE &&
                  airlineCode !== config?.airlineCode
              )
              .sort();
            setAirlineCodes(caAirlineCodes);
            setLoading(prev => ({ ...prev, airlineCodes: false }));
          }
        })
        .catch(
          handleAuthErr(err => {
            logger.error(err);
            message.error(constants.API_ERR_MESSAGE);
            setLoading(prev => ({ ...prev, airlineCodes: false }));
          })
        );
    }
  }, [config]);

  const handleSelect = value => {
    // Cache the available system models per airline code
    setSelectedAirlineCode(value);
    setLoading(prev => ({ ...prev, airlineSystemModels: true }));
    getAirlineSystemModels(value)
      .then(data => {
        if (!!data) setIsValidAirline(!!data?.includes && data.includes(systemModel));
        setLoading(prev => ({ ...prev, airlineSystemModels: false }));
      })
      .catch(
        handleAuthErr(err => {
          logger.error(err);
          message.error(constants.API_ERR_MESSAGE);
          setLoading(prev => ({ ...prev, airlineSystemModels: false }));
        })
      );
  };

  const handleConfirm = () => {
    if (!!selectedAirlineCode) {
      setLoading(prev => ({ ...prev, confirm: true }));
      changeAirlineCode(config, selectedAirlineCode)
        .then(data => {
          updateConfig(data);
          setLoading(prev => ({ ...prev, confirm: false }));
          close();
        })
        .catch(
          handleAuthErr(err => {
            logger.error(err);
            message.error(constants.API_ERR_MESSAGE);
            setLoading(prev => ({ ...prev, confirm: false }));
            close();
          })
        );
    }
  };

  return (
    <Modal
      cancelButtonProps={{ type: 'link', disabled: loading.confirm }}
      closable={false}
      confirmLoading={loading.confirm}
      maskClosable={!loading.confirm}
      okButtonProps={{
        disabled: !selectedAirlineCode || !isValidAirline,
      }}
      okText="Confirm"
      onCancel={close}
      onOk={handleConfirm}
      style={{ display: 'flex', justifyContent: 'center' }}
      title="Change Airline Code"
      visible={visible}
    >
      <span>
        Select an airline:
        <Select
          showSearch
          style={{ marginLeft: '0.5rem', width: 120 }}
          placeholder="Airline"
          optionFilterProp="children"
          onSelect={handleSelect}
          value={selectedAirlineCode}
          disabled={loading.confirm}
          filterOption={selectFilterFn}
          loading={!!loading.airlineCodes || !!loading.airlineSystemModels}
        >
          {!!airlineCodes?.length &&
            airlineCodes.map(airlineCode => (
              <Option key={airlineCode} value={airlineCode}>
                {airlineCode}
              </Option>
            ))}
        </Select>
      </span>
      {!!selectedAirlineCode && !isValidAirline && !loading.airlineSystemModels && (
        <div
          className="ant-form-explain"
          css={css`
            width: 220px;
            color: #f5222d;
            margin: 0.8rem 0 0 0;
            text-align: center;
          `}
        >
          This airline does not support the {systemModel} system model
        </div>
      )}
    </Modal>
  );
};

const selectFilterFn = (input, option) =>
  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

export default AirlineCodeChangeModal;
